import { useState } from 'react'
import { useAuthStore } from '@repo/api-client'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import ChurchIcon from '@mui/icons-material/Church'
import PersonIcon from '@mui/icons-material/Person'
import EmailIcon from '@mui/icons-material/Email'
import SchoolIcon from '@mui/icons-material/School'
import GroupsIcon from '@mui/icons-material/Groups'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FingerprintIcon from '@mui/icons-material/Fingerprint'
import ContactMailIcon from '@mui/icons-material/ContactMail'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'


const UserInfo = () => {
  const { t } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const navigate = useNavigate()

  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [isMobileRolesExpanded, setIsMobileRolesExpanded] = useState(false)

  const copyToClipboard = (key: string, value: any) => {
    if (!value) return
    navigator.clipboard.writeText(String(value))
    setCopiedKey(key)
    setTimeout(() => {
      setCopiedKey(null)
    }, 2000)
  }

  if (!user) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 px-4'>
        <div className='p-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl text-center max-w-md w-full text-white'>
          <div className='h-16 w-16 mx-auto mb-4 bg-amber-500/20 rounded-2xl flex items-center justify-center text-amber-400 border border-amber-400/30'>
            <LockOutlinedIcon style={{ fontSize: 32 }} />
          </div>
          <h1 className='text-2xl font-bold mb-2 text-white'>
            {t('profile.noSession') || 'No Session Found'}
          </h1>
          <p className='text-slate-300 mb-6 text-sm'>
            {t('profile.loginPrompt') ||
              'Please log in to view and manage your profile details.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className='w-full px-5 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-2xl transition-all duration-300 font-semibold shadow-lg shadow-blue-500/30 active:scale-98 cursor-pointer'
          >
            {t('login.buttons.signIn') || 'Go to Login'}
          </button>
        </div>
      </div>
    )
  }

  const roles = user.roles || []
  const hasReqRoles = user.reqRole && user.reqRole.length > 0

  return (
    <div className='min-h-screen bg-slate-100/70 py-6 px-4 sm:px-6 lg:px-8 font-sans'>
      <div className='max-w-6xl mx-auto space-y-6'>
        {/* SERVE Theme Header Banner */}
        <div className='relative overflow-hidden rounded-3xl bg-[#3578d6] text-white shadow-2xl'>
          {/* User Profile Bar (Top Strip) */}
          <div className='relative z-10 px-6 sm:px-8 pt-6 pb-4 border-b border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
            <div className='flex items-center gap-4'>
              {/* User Avatar */}
              <div className='relative'>
                <div className='h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-white/20 ring-2 ring-white/40 flex items-center justify-center text-xl sm:text-2xl font-bold text-white shadow-md overflow-hidden backdrop-blur-sm'>
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt='Profile'
                      className='h-full w-full object-cover'
                    />
                  ) : (
                    <span>
                      {(
                        user.first_name?.[0] ||
                        user.email?.[0] ||
                        'U'
                      ).toUpperCase()}
                    </span>
                  )}
                </div>
                {user.enabled && (
                  <span
                    title='Active User'
                    className='absolute bottom-0 right-0 h-4 w-4 bg-emerald-400 border-2 border-[#3578d6] rounded-full'
                  />
                )}
              </div>

              {/* User Info */}
              <div className='space-y-0.5'>
                <div className='flex flex-wrap items-center gap-2'>
                  <h2 className='text-lg sm:text-xl font-bold text-white leading-tight'>
                    {user.first_name || user.last_name
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : user.email.split('@')[0]}
                  </h2>

                  {/* Desktop Roles: Show all badges */}
                  <div className='hidden sm:flex flex-wrap gap-1'>
                    {roles.map((role) => (
                      <span
                        key={role}
                        className='px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 text-white border border-white/25 backdrop-blur-xs'
                      >
                        {role}
                      </span>
                    ))}
                  </div>

                  {/* Mobile Roles: Collapsed by default when more than 1 role */}
                  {roles.length > 0 && (
                    <div className='flex sm:hidden flex-wrap items-center gap-1'>
                      {roles.length === 1 ? (
                        <span
                          key={roles[0]}
                          className='px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 text-white border border-white/25 backdrop-blur-xs'
                        >
                          {roles[0]}
                        </span>
                      ) : isMobileRolesExpanded ? (
                        <>
                          {roles.map((role) => (
                            <span
                              key={role}
                              className='px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 text-white border border-white/25 backdrop-blur-xs'
                            >
                              {role}
                            </span>
                          ))}
                          <button
                            type='button'
                            onClick={() => setIsMobileRolesExpanded(false)}
                            className='px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/30 hover:bg-white/40 active:scale-95 text-white border border-white/35 backdrop-blur-xs cursor-pointer inline-flex items-center gap-0.5 transition-all'
                            aria-label='Collapse roles'
                          >
                            <span>Collapse</span>
                            <KeyboardArrowUpIcon style={{ fontSize: 13 }} />
                          </button>
                        </>
                      ) : (
                        <div
                          onClick={() => setIsMobileRolesExpanded(true)}
                          className='inline-flex items-center gap-1 cursor-pointer active:scale-95 transition-transform'
                          role='button'
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setIsMobileRolesExpanded(true)
                            }
                          }}
                          aria-label={`Roles: ${roles[0]}, plus ${roles.length - 1} more. Tap to expand.`}
                        >
                          <span
                            className='px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 text-white border border-white/25 backdrop-blur-xs'
                          >
                            {roles[0]}
                          </span>
                          <span
                            className='px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/25 hover:bg-white/35 text-white border border-white/30 backdrop-blur-xs inline-flex items-center gap-0.5'
                          >
                            <span>+{roles.length - 1}</span>
                            <KeyboardArrowDownIcon style={{ fontSize: 13 }} />
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className='flex items-center gap-1.5 text-xs text-blue-100/90'>
                  <EmailIcon style={{ fontSize: 14 }} />
                  <span>{user.email}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions & Language Switcher */}
            <div className='flex items-center gap-2.5 self-end md:self-center'>
              {user.StructID && user.ChurchID && (
                <button
                  onClick={() => navigate('/fast-signup/generate-qr')}
                  className='px-4 py-2 bg-white/95 hover:bg-white active:scale-98 text-[#2860b2] rounded-xl shadow-md transition-all text-xs font-bold flex items-center gap-1.5 cursor-pointer'
                >
                  <QrCode2Icon style={{ fontSize: 18 }} />
                  <span className='hidden sm:inline'>
                    {t('common.helpFriendSignIn') || 'Help Friend Sign In'}
                  </span>
                  <span className='sm:hidden'>QR Sign-in</span>
                </button>
              )}
            </div>
          </div>

          {/* Quick Stats Cards Grid inside the Header Banner */}
          <div className='relative z-10 px-4 sm:px-6 md:px-8 py-6'>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              <StatCard
                label='Account Status'
                value={user.enabled ? 'Active' : 'Suspended'}
                icon={
                  <VerifiedUserIcon
                    style={{ fontSize: 20 }}
                    className={user.enabled ? 'text-emerald-500' : 'text-rose-500'}
                  />
                }
                iconBg={user.enabled ? 'bg-emerald-50' : 'bg-rose-50'}
                badgeClass={
                  user.enabled
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-rose-50 text-rose-700 border-rose-200'
                }
              />
              <StatCard
                label='Member ID'
                value={user.MemID ? `#${user.MemID}` : 'Unlinked'}
                icon={<PersonIcon style={{ fontSize: 20 }} className='text-blue-500' />}
                iconBg='bg-blue-50'
                isUnset={!user.MemID}
              />
              <StatCard
                label='Church ID'
                value={user.ChurchID ? `Church #${user.ChurchID}` : 'Not Assigned'}
                icon={<ChurchIcon style={{ fontSize: 20 }} className='text-purple-500' />}
                iconBg='bg-purple-50'
                isUnset={!user.ChurchID}
              />
              <StatCard
                label='Structure ID'
                value={user.StructID ? `Node #${user.StructID}` : 'Not Assigned'}
                icon={<AccountTreeIcon style={{ fontSize: 20 }} className='text-amber-500' />}
                iconBg='bg-amber-50'
                isUnset={!user.StructID}
              />
            </div>
          </div>
        </div>

        {/* 4 Core Information Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {/* Card 1: Identity & Credentials */}
          <div className='bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow'>
            <div className='flex items-center justify-between pb-4 mb-5 border-b border-slate-100'>
              <div className='flex items-center gap-3'>
                <div className='p-2.5 bg-blue-50 text-blue-600 rounded-xl border border-blue-100'>
                  <FingerprintIcon style={{ fontSize: 22 }} />
                </div>
                <div>
                  <h3 className='text-base font-bold text-slate-800'>
                    {t('profile.identification') || 'Identity & Credentials'}
                  </h3>
                  <p className='text-xs text-slate-400'>
                    System identifiers and authentication keys
                  </p>
                </div>
              </div>
            </div>

            <div className='space-y-3'>
              <CopyableRow
                idKey='id'
                label='Internal User ID'
                value={user.id}
                copiedKey={copiedKey}
                onCopy={copyToClipboard}
              />
              <CopyableRow
                idKey='MemID'
                label='Member Registry ID (MemID)'
                value={user.MemID}
                copiedKey={copiedKey}
                onCopy={copyToClipboard}
              />
              <CopyableRow
                idKey='google_account_id'
                label='Google Account ID'
                value={user.google_account_id}
                copiedKey={copiedKey}
                onCopy={copyToClipboard}
              />
              <CopyableRow
                idKey='email'
                label='Primary Email'
                value={user.email}
                copiedKey={copiedKey}
                onCopy={copyToClipboard}
              />
            </div>
          </div>

          {/* Card 2: Church & Ministry Placement */}
          <div className='bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow'>
            <div className='flex items-center justify-between pb-4 mb-5 border-b border-slate-100'>
              <div className='flex items-center gap-3'>
                <div className='p-2.5 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100'>
                  <ChurchIcon style={{ fontSize: 22 }} />
                </div>
                <div>
                  <h3 className='text-base font-bold text-slate-800'>
                    {t('profile.organization') ||
                      'Church & Organizational Placement'}
                  </h3>
                  <p className='text-xs text-slate-400'>
                    Church structure, cell group & ministry assignments
                  </p>
                </div>
              </div>
            </div>

            <div className='space-y-3'>
              <PlacementRow
                label='Main Church ID'
                value={user.ChurchID}
                icon={
                  <ChurchIcon
                    style={{ fontSize: 18 }}
                    className='text-slate-400'
                  />
                }
              />
              <PlacementRow
                label='Structure Node ID'
                value={user.StructID}
                icon={
                  <AccountTreeIcon
                    style={{ fontSize: 18 }}
                    className='text-slate-400'
                  />
                }
              />
              <PlacementRow
                label='WFL Placement ID'
                value={user.WFLStructID}
                icon={
                  <GroupsIcon
                    style={{ fontSize: 18 }}
                    className='text-slate-400'
                  />
                }
              />
              <PlacementRow
                label='Teacher Assignment ID'
                value={user.TeacherID}
                icon={
                  <SchoolIcon
                    style={{ fontSize: 18 }}
                    className='text-slate-400'
                  />
                }
              />
            </div>
          </div>

          {/* Card 3: System Permissions & Privileges */}
          <div className='bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow'>
            <div className='flex items-center justify-between pb-4 mb-5 border-b border-slate-100'>
              <div className='flex items-center gap-3'>
                <div className='p-2.5 bg-purple-50 text-purple-600 rounded-xl border border-purple-100'>
                  <AdminPanelSettingsIcon style={{ fontSize: 22 }} />
                </div>
                <div>
                  <h3 className='text-base font-bold text-slate-800'>
                    {t('profile.permissions') || 'System Permissions & Access'}
                  </h3>
                  <p className='text-xs text-slate-400'>
                    Administrative privilege levels and access rights
                  </p>
                </div>
              </div>
            </div>

            <div className='space-y-3'>
              <PermissionRow
                label='Account Enabled'
                granted={!!user.enabled}
                grantedText='Active & Verified'
                deniedText='Suspended'
              />
              <PermissionRow
                label='Super Admin Level'
                granted={!!user.isSuperAdmin}
                grantedText='Authorized'
                deniedText='Not Granted'
              />
              <PermissionRow
                label='Event Admin Privileges'
                granted={!!user.isEventAdmin}
                grantedText='Authorized'
                deniedText='Not Granted'
              />
              <PermissionRow
                label='Event Staff Operations'
                granted={!!user.isEventAccount}
                grantedText='Authorized'
                deniedText='Not Granted'
              />
            </div>
          </div>

          {/* Card 4: Requested Roles & Community Status */}
          <div className='bg-white rounded-2xl p-6 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow flex flex-col justify-between'>
            <div>
              <div className='flex items-center justify-between pb-4 mb-5 border-b border-slate-100'>
                <div className='flex items-center gap-3'>
                  <div className='p-2.5 bg-amber-50 text-amber-600 rounded-xl border border-amber-100'>
                    <ContactMailIcon style={{ fontSize: 22 }} />
                  </div>
                  <div>
                    <h3 className='text-base font-bold text-slate-800'>
                      {t('profile.requestedRoles') || 'Role Requests'}
                    </h3>
                    <p className='text-xs text-slate-400'>
                      Pending privilege escalations & role approvals
                    </p>
                  </div>
                </div>
              </div>

              {hasReqRoles ? (
                <div className='space-y-3'>
                  <p className='text-xs font-medium text-slate-500'>
                    The following roles have been requested and are awaiting
                    administrative approval:
                  </p>
                  <div className='flex flex-wrap gap-2 pt-1'>
                    {user.reqRole?.map((role: string, idx: number) => (
                      <span
                        key={idx}
                        className='inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-xs font-bold'
                      >
                        <span className='h-1.5 w-1.5 rounded-full bg-amber-500' />
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className='py-8 flex flex-col items-center justify-center text-center'>
                  <div className='h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-2'>
                    <CheckIcon style={{ fontSize: 22 }} />
                  </div>
                  <p className='text-sm font-semibold text-slate-700'>
                    No Pending Role Requests
                  </p>
                  <p className='text-xs text-slate-400 mt-0.5'>
                    Your account permissions are up to date.
                  </p>
                </div>
              )}
            </div>

            {/* Quick helper banner if user has structure IDs */}
            {user.StructID && user.ChurchID && (
              <div className='mt-4 pt-4 border-t border-slate-100'>
                <div
                  onClick={() => navigate('/fast-signup/generate-qr')}
                  className='p-3.5 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-xl border border-blue-100/80 cursor-pointer flex items-center justify-between transition-colors group'
                >
                  <div className='flex items-center gap-3'>
                    <div className='h-8 w-8 rounded-lg bg-[#3578d6] text-white flex items-center justify-center shadow-sm'>
                      <QrCode2Icon style={{ fontSize: 18 }} />
                    </div>
                    <div>
                      <p className='text-xs font-bold text-slate-900'>
                        {t('generateQr.title') || 'Help Friend Log In'}
                      </p>
                      <p className='text-[11px] text-blue-700'>
                        Generate 15-minute quick registration QR code
                      </p>
                    </div>
                  </div>
                  <ArrowForwardIcon
                    style={{ fontSize: 16 }}
                    className='text-[#3578d6] group-hover:translate-x-1 transition-transform'
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer info bar */}
        <div className='bg-white rounded-2xl px-6 py-4 border border-slate-200/80 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-500 font-medium'>
          <div className='flex items-center gap-2'>
            <span className='font-semibold text-slate-700'>
              Acts of Christ Church
            </span>
            <span>•</span>
            <span>Authentication & Identity Management</span>
          </div>

          <div className='flex items-center gap-2'>
            <span className='h-2 w-2 rounded-full bg-emerald-500 animate-ping' />
            <span className='text-slate-600 font-semibold'>
              Secure Data Channel Active
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/* --- Reusable Sub-components --- */

interface StatCardProps {
  label: string
  value: string
  icon: React.ReactNode
  iconBg?: string
  badgeClass?: string
  isUnset?: boolean
}

const StatCard = ({
  label,
  value,
  icon,
  iconBg = 'bg-slate-50',
  badgeClass = '',
  isUnset = false,
}: StatCardProps) => (
  <div className='bg-white p-4.5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow'>
    <div className='flex items-center justify-between mb-3'>
      <span className='text-xs font-bold text-slate-400 uppercase tracking-wider'>
        {label}
      </span>
      <div className={`p-1.5 rounded-xl ${iconBg} flex items-center justify-center`}>
        {icon}
      </div>
    </div>
    <div className='flex items-center gap-2'>
      {badgeClass ? (
        <span
          className={`px-3 py-0.5 rounded-full text-xs font-bold border ${badgeClass}`}
        >
          {value}
        </span>
      ) : (
        <span
          className={`text-base sm:text-lg font-black ${
            isUnset
              ? 'text-slate-400 italic text-sm font-normal'
              : 'text-slate-900'
          }`}
        >
          {value}
        </span>
      )}
    </div>
  </div>
)

interface CopyableRowProps {
  idKey: string
  label: string
  value: any
  copiedKey: string | null
  onCopy: (key: string, value: any) => void
}

const CopyableRow = ({
  idKey,
  label,
  value,
  copiedKey,
  onCopy,
}: CopyableRowProps) => {
  const isUnset = value === null || value === undefined || value === ''
  const isCopied = copiedKey === idKey

  return (
    <div className='p-3 bg-slate-50/70 hover:bg-slate-50 rounded-xl border border-slate-100 flex items-center justify-between gap-3 transition-colors'>
      <div className='min-w-0 flex-1'>
        <p className='text-[11px] font-bold text-slate-400 uppercase tracking-wide'>
          {label}
        </p>
        <p className='text-sm font-semibold text-slate-800 truncate'>
          {isUnset ? (
            <span className='text-slate-400 font-normal italic'>Unset</span>
          ) : (
            String(value)
          )}
        </p>
      </div>

      {!isUnset && (
        <button
          onClick={() => onCopy(idKey, value)}
          title='Copy value'
          className='p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer shrink-0'
        >
          {isCopied ? (
            <span className='flex items-center gap-1 text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200'>
              <CheckIcon style={{ fontSize: 13 }} /> Copied
            </span>
          ) : (
            <ContentCopyIcon style={{ fontSize: 16 }} />
          )}
        </button>
      )}
    </div>
  )
}

interface PlacementRowProps {
  label: string
  value: any
  icon: React.ReactNode
}

const PlacementRow = ({ label, value, icon }: PlacementRowProps) => {
  const isUnset = value === null || value === undefined || value === ''

  return (
    <div className='p-3 bg-slate-50/70 rounded-xl border border-slate-100 flex items-center justify-between gap-3'>
      <div className='flex items-center gap-2.5'>
        <div className='shrink-0'>{icon}</div>
        <span className='text-xs font-semibold text-slate-600'>{label}</span>
      </div>

      <div>
        {isUnset ? (
          <span className='text-xs text-slate-400 italic'>Not Assigned</span>
        ) : (
          <span className='text-xs font-bold text-slate-800 bg-white px-2.5 py-1 rounded-lg border border-slate-200 shadow-2xs'>
            #{value}
          </span>
        )}
      </div>
    </div>
  )
}

interface PermissionRowProps {
  label: string
  granted: boolean
  grantedText: string
  deniedText: string
}

const PermissionRow = ({
  label,
  granted,
  grantedText,
  deniedText,
}: PermissionRowProps) => (
  <div className='p-3 bg-slate-50/70 rounded-xl border border-slate-100 flex items-center justify-between gap-3'>
    <div className='flex items-center gap-2'>
      <div
        className={`h-2 w-2 rounded-full ${
          granted
            ? 'bg-emerald-500 shadow-sm shadow-emerald-400'
            : 'bg-slate-300'
        }`}
      />
      <span className='text-xs font-semibold text-slate-700'>{label}</span>
    </div>

    <div>
      {granted ? (
        <span className='inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full'>
          <CheckIcon style={{ fontSize: 13 }} />
          {grantedText}
        </span>
      ) : (
        <span className='text-xs font-medium text-slate-400 bg-slate-100 px-2.5 py-0.5 rounded-full'>
          {deniedText}
        </span>
      )}
    </div>
  </div>
)

export default UserInfo
